import React, { useCallback, useState } from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { MyDataGrid } from "../../common/components";
import clientGraphql from "../../../common/apollo-graphql";
import HelperUtils from "../../common/services/HelperUtils";
import { GET_PENDING_ORDERS } from "../gql/query";
import { UPDATE_ORDER_STATUS } from "../gql/mutation";

export default function ProviderOrdersTable({ signature }) {
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const { data, loading, error, refetch } = useQuery(GET_PENDING_ORDERS, {
    client: clientGraphql,
    fetchPolicy: "cache-and-network",
  });

  const [updateOrderStatus, { loading: mutationLoading }] = useMutation(
    UPDATE_ORDER_STATUS,
    {
      client: clientGraphql,
      onCompleted: () => refetch(),
      onError: (err) => {
        setSnackbar({ open: true, message: err.message, severity: "error" });
      },
    }
  );

  const rows =
    data?.orders?.map((order) => ({
      id: order.id,
      uuid: order.id,
      name: order.patient?.full_name || "",
      mrn: order.mrn || "",
      birthdate: HelperUtils.formatBirthdate(order.patient?.details?.birthdate),
      diagnosis: order.diagnosis?.description || "",
      clinic: order.referrer?.name || "",
    })) || [];

  const handleApprove = async (row) => {
    try {
      await updateOrderStatus({ variables: { uuid: row.uuid, status: "order_approved" } });
      setSnackbar({ open: true, message: "Order approved successfully", severity: "success" });
    } catch (err) {
      console.error("Error approving order", err);
    }
  };

  const handleDecline = async (row) => {
    try {
      await updateOrderStatus({ variables: { uuid: row.uuid, status: "not_approved" } });
      setSnackbar({ open: true, message: "Order not approved.", severity: "warning" });
    } catch (err) {
      console.error("Error declining order", err);
    }
  };

  const columns = [
    { field: "name", headerName: "Patient Name", flex: 2 },
    { field: "mrn", headerName: "MRN", flex: 1 },
    { field: "birthdate", headerName: "Date of Birth", flex: 1 },
    { field: "diagnosis", headerName: "Diagnosis", flex: 2 },
    { field: "clinic", headerName: "Clinic Referrer", flex: 3 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 2,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={mutationLoading || !signature}
            style={{ marginRight: 8 }}
            onClick={() => handleApprove(params.row)}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={mutationLoading || !signature}
            onClick={() => handleDecline(params.row)}
          >
            Decline
          </Button>
        </Box>
      ),
    },
  ];

  const handleFilterChanged = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const filteredRows = rows.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  if (error) return <div>Error loading orders</div>;

  return (
    <Box>
      <MyDataGrid
        data={{ rows: filteredRows, count: rows.length }}
        columns={columns}
        loading={loading || mutationLoading}
        autoHeight
        disableSelectionOnClick
        onFilterChanged={handleFilterChanged}
        disableColumnMenu
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity={snackbar.severity}
          variant="outlined"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            width: "100%",
            bgcolor: "primary.main",
            color: "primary.contrastText"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
